import axios from './axios-with-authentication';
import * as backend from './backend-types';
import { ClinicalUnit, Cohort } from '../types';
import { API_BASE_PATH } from './constants';
import { getCurrentUser } from './auth-user';
import { getPortalOrganizationId } from './portal-adapter';
import { fetchAllPagedContent } from './pagination';
import { log } from './log';

const clinicalUnitCohortsCache = new Map<string, Cohort[]>();

function mapCohortsFromBackend(
  cohorts: backend.PortalClinicalUnitCohortResponse[] | undefined,
  clinicalUnitId: string,
): Cohort[] {
  return (Array.isArray(cohorts) ? cohorts : [])
    .map((cohort, index) => {
      const id = String(cohort.id || '').trim();
      const name = String(cohort.name || '').trim();
      if (!id || !name) return null;
      return {
        id,
        name,
        clinicalUnitId,
        isDefault: index === 0,
      } as Cohort;
    })
    .filter((cohort): cohort is Cohort => cohort !== null);
}

function mapClinicalUnitFromBackend(
  raw: backend.PortalClinicalUnitEntryResponse,
): ClinicalUnit | null {
  const name = String(raw.name || '').trim();
  const id = String(raw.id || '').trim();
  if (!id || !name) return null;
  return { id, name };
}

export function getCachedCohorts(clinicalUnitId: string): Cohort[] | undefined {
  const normalizedClinicalUnitId = String(clinicalUnitId || '').trim();
  if (!normalizedClinicalUnitId) return undefined;
  if (!clinicalUnitCohortsCache.has(normalizedClinicalUnitId)) return undefined;
  const cached = clinicalUnitCohortsCache.get(normalizedClinicalUnitId) || [];
  return cached.map((cohort) => ({ ...cohort }));
}

export async function getClinicalUnits(): Promise<ClinicalUnit[]> {
  try {
    const user = await getCurrentUser();
    const hcoId = getPortalOrganizationId(user);
    if (!hcoId) return [];

    const content = await fetchAllPagedContent<
      backend.PortalClinicalUnitEntryResponse,
      backend.PortalClinicalUnitPageResponse
    >({
      fetchPage: async (pageNumber, pageSize) => {
        const params = {
          page: pageNumber,
          ...(pageSize !== undefined ? { size: pageSize } : {}),
        };
        const { data } = await axios.get<backend.PortalClinicalUnitPageResponse>(
          `${API_BASE_PATH}/hcos/${hcoId}/clinical-units`,
          { params },
        );
        return data;
      },
    });

    const mappedUnits: ClinicalUnit[] = [];
    clinicalUnitCohortsCache.clear();
    content.forEach((rawClinicalUnit) => {
      const mappedUnit = mapClinicalUnitFromBackend(rawClinicalUnit);
      if (!mappedUnit) return;
      mappedUnits.push(mappedUnit);
      clinicalUnitCohortsCache.set(
        mappedUnit.id,
        mapCohortsFromBackend(rawClinicalUnit.cohorts, mappedUnit.id),
      );
    });

    return mappedUnits;
  } catch (err) {
    log('clinicalunits/getClinicalUnits', err instanceof Error ? err : new Error(String(err)));
    clinicalUnitCohortsCache.clear();
    return [];
  }
}
